import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Clock, Gift, ArrowRight } from 'lucide-react';
import { useBillingPageData } from '@/hooks/useBilling';

export const TrialBanner = () => {
  const { t } = useTranslation();
  const { data, isLoading } = useBillingPageData();

  if (isLoading) return null;

  const account = data?.account;
  if (!account) return null;

  const status = account.status;
  const trialEndsAt = account.trial_ends_at;
  const freeReturns = account.free_returns_remaining ?? 0;

  // Only show for trial or trial_expired status
  if (status !== 'trial' && status !== 'trial_expired') return null;

  const isExpired = status === 'trial_expired';
  let daysRemaining = 0;

  if (trialEndsAt && !isExpired) {
    const diff = new Date(trialEndsAt).getTime() - Date.now();
    daysRemaining = Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
    if (daysRemaining === 0 && diff > 0) daysRemaining = 1;
  }

  return (
    <div className={`mx-4 mt-4 rounded-card border p-4 animate-fade-in ${
      isExpired
        ? 'border-error/30 bg-error/5'
        : 'border-primary/30 bg-primary/5'
    }`}>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Clock className={`h-5 w-5 shrink-0 ${isExpired ? 'text-error' : 'text-primary'}`} />
          <div>
            <p className={`text-sm font-medium ${isExpired ? 'text-error' : ''}`}>
              {isExpired
                ? t('subscription.trialExpired')
                : daysRemaining <= 1
                  ? t('subscription.trialBannerLastDay')
                  : t('subscription.trialBanner', { days: daysRemaining })
              }
            </p>
            {freeReturns > 0 && (
              <p className="text-xs text-base-content/60 flex items-center gap-1 mt-0.5">
                <Gift className="h-3 w-3" />
                {t('subscription.freeReturns', { remaining: freeReturns })}
              </p>
            )}
            {freeReturns === 0 && !isExpired && (
              <p className="text-xs text-error flex items-center gap-1 mt-0.5">
                {t('subscription.freeReturnsExhausted')}
              </p>
            )}
          </div>
        </div>
        <Link
          to="/subscription"
          className="btn btn-sm bg-gradient-orange text-black gap-1 shrink-0"
        >
          {t('subscription.selectPlan')}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </div>
  );
};
